import type { SxProps } from "@mui/system";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
} from "@vapor/react-material";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDeleteCustomFulfilment } from "../../../core/api/use-delete-custom-fulfilment/useDeleteCustomFulfilment";
import { useSuccessNotification } from "../../../core/hooks/useSuccessNotification";

interface DeleteCustomFulfilmentProps {
    children?: string | JSX.Element | JSX.Element[];
    levyId: string;
    onCancel?: (event: React.MouseEvent<HTMLButtonElement>) => unknown;
    onDelete?: (event: React.MouseEvent<HTMLButtonElement>) => unknown;
    sx?: SxProps;
}

export const DeleteCustomFulfilment = ({
    children,
    levyId,
    onCancel,
    onDelete,
    sx,
}: DeleteCustomFulfilmentProps): ReactElement => {
    const { t } = useTranslation();
    const { showTextMessage } = useSuccessNotification();

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const { mutateAsync: deleteCustomFulfilment, isPending: loading } =
        useDeleteCustomFulfilment();

    const onClickCancelDialog = (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        setIsDialogOpen(false);
        if (onCancel) onCancel(event);
    };

    const onClickConfirmDialog = async (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        try {
            await deleteCustomFulfilment({ levyId: levyId });
        } catch {
            return;
        }

        showTextMessage(
            t("components.delete_custom_fulfilment.notification.success"),
        );

        setIsDialogOpen(false);
        if (onDelete) onDelete(event);
    };

    return (
        <>
            <Dialog open={isDialogOpen} onClose={onClickCancelDialog}>
                <DialogTitle>
                    {t("components.delete_custom_fulfilment.dialog.title")}
                </DialogTitle>
                <Divider variant="fullWidth" />
                <DialogContent>
                    <DialogContentText>
                        {t(
                            "components.delete_custom_fulfilment.dialog.content",
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        disabled={loading}
                        onClick={onClickCancelDialog}
                        variant="outlined"
                    >
                        {t(
                            "components.delete_custom_fulfilment.dialog.actions.cancel",
                        )}
                    </Button>
                    <Button
                        disabled={loading}
                        color="error"
                        onClick={onClickConfirmDialog}
                        variant="contained"
                    >
                        {t(
                            "components.delete_custom_fulfilment.dialog.actions.confirm",
                        )}
                        {loading && (
                            <CircularProgress
                                color="inherit"
                                sx={{ ml: 1 }}
                                size={16}
                            />
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
            <Box onClick={() => setIsDialogOpen(true)} sx={sx}>
                {children && children}
            </Box>
        </>
    );
};
